import { ChangeEvent, useRef, useState, MouseEvent, useEffect, useMemo } from "react";
import Button from "../common/Button";
import commonStyles from "./CreateMapSetting.module.css";
import styles from "./MapComplete.module.css";
import inputStyle from "../common/TextArea.module.css";
import PreviousBtn from "../common/PreviousBtn";
import TextArea from "../common/TextArea";
import ContentTitle from "../common/ContentTitle";
import MapKeywordItem from "../publicmap/MapKeywordItem";
import Icon from "../common/Icon";
import { useCookies } from "react-cookie";
import { path, validateDomain, validateEmailId, validateFullEmail } from "../common/Common";

import html2canvas from "html2canvas";
import DataProvider from "../../data_provider/DataProvider";
import { SaveMapReqBody } from "../../data_provider/gateways/MapGateway";
import { MapSaveData } from "./CreateMap";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";

import LoadingSpinner from "../common/LoadingSpinner";
import PreviewMap from "./PreviewMap";
import { Category } from "../../components/common/Common";
import CategoryGroup from "../map/CategoryGroup";

import PolicyModal from "../login/PolicyModal";
import TextInput from "../common/TextInput";

export interface CreateMapInfo {
    title: string;
    keyword: string[];
    desc: string;
    email: string;
}

interface MapCompleteProps {
    progressProcess: (n: number) => void;
    data: { [key: string]: object | string[][] }; // mapSettingData, marker정보, data: initMapData(엑셀기반 지도 정보)
    saveData: MapSaveData;
    geojson: string;
    categories: Category[];
    processIdx?: number; // 프로세스 인덱스 추가
}

/**
 * @author YoungJunKim
 * @param props
 * @returns
 */
function MapComplete(props: MapCompleteProps) {
    const MAX_KEYWORDS = 3;
    const MAX_KEYWORD_LENGTH = 10;

    const [cookies] = useCookies(["KMUI", "KMNM"]);

    const userId = cookies.KMUI ?? undefined;
    const emailOpt: string[] = ["naver.com", "gmail.com", "daum.net", "ksic.net", "직접입력"];

    const categoriesWithAll = useMemo(() => {
        if (!props.categories.length) return [];

        return props.categories[0].label === "전체" ? props.categories : [{ label: "전체", disabled: false }, ...props.categories];
    }, [props.categories]);

    // ----------------
    // -   useState   -
    // ----------------

    const [selectedCategory, setSelectedCategory] = useState(0);
    const [keywords, setKeywords] = useState<string[]>([]);
    const [optionIdx, setOptionIdx] = useState<number>(-1);
    const [isValidHost, setIsValidHost] = useState(true);
    const [isValidDomain, setIsValidDomain] = useState(true);
    const [emailId, setEmailId] = useState(userId ? userId.substring(0, userId.indexOf("@")) : "");
    const [emailDomain, setEmailDomain] = useState(userId ? userId.substring(userId.indexOf("@") + 1) : emailOpt[0]);

    const [mapSaveInfo, setMapSaveInfo] = useState({
        title: "",
        keyword: [],
        emailId: "",
        emailDomail: "",
        desc: "",
        terms: false,
    });

    const [validation, setValidation] = useState({
        title: false,
        keyword: false,
        email: false,
        desc: false,
        terms: false,
    });

    const { ModalComponent, openModal, closeModal } = useModal();

    const [isLoading, setIsLoading] = useState(false);

    const [stableGeojson, setStableGeojson] = useState("");
    const [showCategoryGroup, setShowCategoryGroup] = useState(true);
    const [keywordContent, setKeywordContent] = useState<string>();

    const formRef = useRef<HTMLFormElement>(null);
    const keywordRef = useRef<HTMLInputElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    // -----------------
    // -   useEffect   -
    // -----------------

    useEffect(() => {
        if (props.geojson) {
            try {
                const parsed = JSON.parse(props.geojson);

                const labelFieldKey = props.saveData?.metadataField?.placeName;

                parsed.features.forEach((feature: any) => {
                    feature.properties.label = feature.properties[labelFieldKey];
                });

                setStableGeojson(JSON.stringify(parsed));
            } catch (e) {
                console.warn("geojson 파싱 실패", e);
            }
        }
    }, [props.geojson, props.saveData]);

    // email 도메인 직접입력 선택 시 검증 false로 변경
    useEffect(() => {
        if (optionIdx === emailOpt.length - 1) {
            setValidation({ ...validation, email: false });
            setIsValidDomain(false);
            setEmailDomain("");
        }
    }, [optionIdx]);

    // 키워드 검증
    useEffect(() => {
        checkValidation();
    }, [keywords]);

    // 최종 검증
    const isValid = () => {
        return Object.values(validation).filter((value: boolean) => !value).length === 0;
    };

    // 검증 변수 변경
    const checkValidation = () => {
        if (!formRef.current) return false;
        const formData = new FormData(formRef.current);
        setValidation({
            ...validation,
            title: formData.get("title") != null && formData.get("title") !== "",
            keyword: keywords.length > 0,
            email: isValidDomain && isValidDomain,
            desc: formData.get("desc") != null && formData.get("desc") !== "",
        });
    };

    const addKeyword = () => {
        // const value = keywordRef.current ? keywordRef.current.value : "";

        // 250623 | smyun | 10글자 제한, 띄어쓰기 제거 추가 (키워드 3개 이상 입력 제한)
        const value = keywordRef.current?.value?.trim();
        if (!value) return;
        if (value.length > MAX_KEYWORD_LENGTH || keywords.length >= MAX_KEYWORDS || keywords.includes(value)) {
            keywordRef.current!.value = "";
            return;
        }

        setKeywords([...keywords, value]); // 키워드 추가
        setKeywordContent("");

        keywordRef.current!.value = "";
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault(); // 스페이스, 엔터키 추가 입력 막기
            addKeyword();
        }
    };

    // 현재 입력중인 키워드 글자수 표출 | 250708 | smyun
    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        const maxLength = e.currentTarget.maxLength;
        const _content = value.length > maxLength ? value.slice(0, maxLength) : value;
        setKeywordContent(_content);
    };

    // 이메일 검증
    const onChangeEmail = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // Email Id 입력체크
        const value = e.target.value;
        const checkResult = validateEmailId(value);

        setEmailId(value);
        setIsValidHost(checkResult);
        isValidDomain && setValidation({ ...validation, email: checkResult });
        // validateCheckFullEmail(value, emailDomain);
    };

    const onChangeDomain = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = e.target.value;
        const checkResult = validateDomain(value);
        // setValidation({
        //     ...validation,
        //     email: validation.email && new RegExp(/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:.[a-zA-Z]{2,})+$/).test(e.currentTarget.value),
        // });
        setEmailDomain(value);
        setIsValidDomain(checkResult);
        isValidHost && setValidation({ ...validation, email: checkResult });
        // validateCheckFullEmail(emailId, value);
    };

    // 이메일 도메인 select박스 변경 시
    const onChangeOption = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setOptionIdx(e.currentTarget.selectedIndex);

        setEmailDomain(value);
        setIsValidDomain(true);
        isValidHost && setValidation({ ...validation, email: true });
    };

    // const validateCheckFullEmail = (id: string, domain: string) => {
    //     const email = `${id}@${domain}`;
    //     const result: boolean = validateFullEmail(email);
    //     setValidation({ ...validation, email: result });
    //     // setIsValidHost(result);
    //     // result && checkValidation();
    // };

    const onChangeTextArea = (value: string) => {
        checkValidation();
    };

    const onChangeAgreement = (e: ChangeEvent<HTMLInputElement>) => {
        setValidation({
            ...validation,
            terms: e.currentTarget.checked,
        });
    };

    // const onClickTerms = () => {
    //     alert("이용약관 페이지");
    // };

    const save = async (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (!formRef.current) return;

        openModal({
            title: "지도를 저장중입니다...",
            children: <LoadingSpinner />,
        });
        setIsLoading(true);

        try {
            const formData = new FormData(formRef.current);

            userId && formData.set("userId", userId);

            if (optionIdx === emailOpt.length - 1) {
                formData.set("domain", formData.get("domain-custom") as string);
                formData.delete("domain-custom");
            }

            Object.keys(props.data).forEach((key: string) => {
                formData.set(key, JSON.stringify(props.data[key]));
            });

            formData.set("keyword", keywords.join(","));

            const title = `${formData.get("title")}`;
            const desc = `${formData.get("desc")}`;
            const email = `${formData.get("email")}@${formData.get("domain")}`;

            const thumbnail = await captureMapThumbnail();

            if (thumbnail) {
                formData.set("thumbnail", thumbnail);
            }

            const keyword = keywords;

            // 250619 | smyun | 비회원인 경우 무조건 공개지도, 로그인 한 경우 선택에 따라 설정되도록 변경
            const privateMap = !userId ? 0 : formData.get("public") ? 0 : 1;
            // const privateMap = props.saveData?.private;

            const mapType = props.saveData?.mapType;

            const sld = JSON.stringify(props.saveData?.markerInfo); // 마커 정보(필드, 스타일)

            const infoStyle = props.saveData?.infoStyle; // 주요정보 스타일

            const category = props.saveData?.category;
            const metadata = props.saveData?.metadataField; // 필드명
            const geoJSON = props.saveData?.geojson; // 마커 위치 및 정보

            console.log("[formData]:\n" + `${JSON.stringify(Object.fromEntries(formData))}`);

            const saveMapReqBody: SaveMapReqBody = {
                title,
                keyword,
                desc,
                email,
                sld,
                infoStyle,
                geoJSON,
                metadata,
                category,
                mapType,
                privateMap,
                thumbnail: thumbnail || "",
            };

            console.log("[saveMapReqBody]:\n" + `${JSON.stringify(saveMapReqBody.metadata)}`);

            const result = await DataProvider.SaveMap(saveMapReqBody); // 저장

            if (result.flag) {
                console.log("지도 저장에 성공 했습니다.");

                openModal({
                    title: "안내",
                    children: "지도 저장에 성공 했습니다.",
                    onCloseNavigatePath: path.PUBLIC_MAPLIST, // 모달창 닫히면 공개지도로 이동
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
            } else {
                openModal({
                    title: "안내",
                    children: <p>{result.desc}</p>,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
            }
        } catch (error) {
            openModal({
                title: "안내",
                children: <p>지도를 저장하는 중 오류가 발생했습니다.</p>,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const captureMapThumbnail = async (): Promise<string | null> => {
        if (!previewRef.current) {
            console.error("지도 미리보기 캡처 대상이 존재하지 않습니다.");
            return null;
        }

        setShowCategoryGroup(false);
        await new Promise((res) => setTimeout(res, 100));

        try {
            const canvas = await html2canvas(previewRef.current, {
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
                width: previewRef.current.clientWidth,
                height: previewRef.current.clientHeight,
                backgroundColor: null,
            });
            const base64 = canvas.toDataURL("image/png").replace("data:image/png;base64,", "");
            return base64;
        } catch (error) {
            console.error("지도 미리보기 캡처 실패: " + error);
            return null;
        } finally {
            setShowCategoryGroup(true);
        }
    };

    return (
        <>
            <ModalComponent />
            <div ref={previewRef} className={`${commonStyles.wrapPreView} ${styles.previewMapWrapper}`}>
                <PreviewMap
                    mapType={props.saveData.mapType}
                    geojson={stableGeojson}
                    categories={categoriesWithAll}
                    selectedCategory={selectedCategory}
                    metadata={props.saveData.metadataField}
                />
                {showCategoryGroup && (
                    <div id="category-group" style={{ position: "absolute", top: 10, left: 10, zIndex: 10, width: "100%" }}>
                        <CategoryGroup categories={categoriesWithAll} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
                    </div>
                )}
            </div>

            <div className={`${commonStyles.wrap} undraggable`}>
                <div className={commonStyles.container}>
                    <div className={commonStyles.title}>
                        <ContentTitle title="지도정보입력" />
                    </div>
                    <form className={styles.content} ref={formRef} method="POST">
                        <div className={`${inputStyle.content}`}>
                            <label>아이디</label>
                            <input className={inputStyle.input} name="userId" readOnly value={userId ?? "비회원"} />
                        </div>
                        <TextArea name="title" getInputValue={onChangeTextArea} maxLength={50} rows={1} wrap="off" type="title" label="제목" />
                        <div className={`${inputStyle.content}`}>
                            <label>키워드</label>
                            <div className={`${inputStyle.content} ${inputStyle.column}`}>
                                <div className={inputStyle.content}>
                                    <div className={styles.rowInPortraitMode}>
                                        <div className={`${styles.keywordContent}`}>
                                            <input
                                                className={`${styles.keywordInput}`}
                                                name="keyword"
                                                ref={keywordRef}
                                                value={keywordContent}
                                                onKeyDown={handleKeyDown}
                                                onChange={onInputChange}
                                                maxLength={10}
                                                disabled={keywords.length >= MAX_KEYWORDS ? true : false}
                                            />
                                            <div className={`${styles.wordLimit}`}>
                                                {keywordContent?.length ?? 0}/{10}
                                            </div>
                                        </div>
                                        <span>
                                            {keywords.length}/{MAX_KEYWORDS}
                                        </span>
                                    </div>
                                    <div className={styles.centerInPortraitMode}>
                                        <Button label="추가" size="small" onClick={addKeyword} />
                                    </div>
                                </div>

                                {keywords.length ? (
                                    <div className={styles.keywords}>
                                        {keywords.map((keyword: string, index: number) => {
                                            const removeKeyword = (e: MouseEvent<HTMLDivElement>) => {
                                                const _keywords = keywords;
                                                _keywords.splice(index, 1);
                                                setKeywords([..._keywords]);
                                                console.log("[_keywords]:\n" + _keywords);

                                                checkValidation();
                                            };
                                            return (
                                                <div className={styles.keyword} key={`keyword_${index}`}>
                                                    <MapKeywordItem title={keyword} size="small" />
                                                    <div onClick={removeKeyword}>
                                                        <Icon name="close" size="small" cursor="pointer" color="grey" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                        <TextArea
                            name="desc"
                            maxLength={200}
                            rows={7}
                            label="설명"
                            getInputValue={onChangeTextArea}
                            isAIGenerator={true}
                            mapData={Array.isArray(props.data["data"]) ? props.data["data"] : []}
                            processIdx={props.processIdx}
                        />
                        <div className={`${inputStyle.content}`}>
                            <label>이메일</label>
                            <div className={`${inputStyle.content} ${styles.centerInPortraitMode}`}>
                                {/* <input className={inputStyle.input} name="email" onChange={onChangeEmail} /> */}
                                <TextInput
                                    name="email"
                                    value={emailId}
                                    placeholder="이메일"
                                    onChange={onChangeEmail}
                                    isValid={isValidHost}
                                    validMsg={"이메일 아이디 형식을 확인해주세요."}
                                />
                                {"@"}
                                <div className={styles.email}>
                                    {optionIdx === emailOpt.length - 1 && (
                                        <div className={styles.customTextInput}>
                                            <TextInput
                                                name="domain-custom"
                                                onChange={onChangeDomain}
                                                placeholder="직접입력"
                                                value={emailDomain}
                                                isValid={isValidDomain}
                                                validMsg={"이메일 도메인 형식을 확인해주세요."}
                                            />
                                        </div>
                                        // <input className={` ${styles.edit}`} name="domain-custom" onChange={onChangeDomain} />
                                    )}
                                    <select
                                        name="domain"
                                        className={`${styles.select} ${!isValidDomain ? styles.invalidSelect : ""}`}
                                        onChange={onChangeOption}
                                        defaultValue={emailDomain}
                                    >
                                        {emailOpt.map((domain: string, index: number) => {
                                            return (
                                                <option value={domain} key={`domain_${index}`}>
                                                    {domain}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className={`${inputStyle.content}`}>
                            <label>공개여부</label>
                            <div className={styles.rowInPortraitMode}>
                                <input className={styles.checkbox} type="checkbox" name="public" defaultChecked disabled={userId ? false : true} />
                                <div>공개</div>
                            </div>
                            <div className={styles.desc}>공개형식의 지도는 단일 아이콘으로 지점을 표시합니다.</div>
                        </div>
                        <div className={`${inputStyle.content}`}>
                            <label>이용약관</label>
                            <div className={styles.rowInPortraitMode}>
                                <input
                                    className={styles.checkbox}
                                    checked={validation.terms}
                                    type="checkbox"
                                    name="terms"
                                    onChange={onChangeAgreement}
                                />
                                <div>
                                    <span
                                        className={styles.terms}
                                        onClick={() =>
                                            openModal({
                                                title: "모두의 지도 서비스 이용약관 동의",
                                                children: <PolicyModal checkBoxClicked={onChangeAgreement} isAccepted={validation.terms} />,
                                                modalButtons: (
                                                    <ModalButtons
                                                        buttonCount={1}
                                                        buttons={[
                                                            {
                                                                label: "확인",
                                                                color: "Primary",
                                                                onClick: () => closeModal(),
                                                            },
                                                        ]}
                                                    />
                                                ),
                                                size: "wide",
                                            })
                                        }
                                    >
                                        {"[이용약관]"}
                                    </span>
                                    에 동의합니다.
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className={commonStyles.footer}>
                        <div className={commonStyles.wrapProgressBtn}>
                            <div className={`${commonStyles.activeBtn}`}>
                                <PreviousBtn progressProcess={props.progressProcess} />
                            </div>
                            <div className={`${commonStyles.activeBtn}`}>
                                <Button
                                    label={"저장"}
                                    size={"large"}
                                    background={isValid() ? "var(--main-color)" : "var(--light-grey)"}
                                    onClick={isValid() ? save : undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
export default MapComplete;
